import { useState } from 'react';
import { formatDateRange } from '../../../utils/formatDateTime';
import { useColorScheme } from '@mui/material/styles';
import {
  AVAILABILITY,
  ENGINE_TYPES,
  formatSpecificationValue,
  getBookingPeriod,
  getBookingPickupState,
  getMachineAvailability,
  isBookingOverdueReturn,
  SPECIFICATION_KEYS,
  STATUSES,
  type BookingPeriod,
} from '../constants';
import { bookingGroup, bookingGroupDark } from '../colors';
import {
  Button,
  ButtonBase,
  Chip,
  Collapse,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import AppDialog from '../../../components/AppDialog';
import ConfirmDeleteDialog from '../../../components/ConfirmDeleteDialog';
import ExpandableChipList from '../../../components/ExpandableChipList';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import MailOutlineIcon from '@mui/icons-material/MailOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import EditIcon from '@mui/icons-material/Edit';
import TagIcon from '@mui/icons-material/Tag';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import type { Category, DetailActionType, Machine, MachineOption, MachineUpdate, Supplier } from '../types';
import type { Settings } from '../../settings/types';
import { DETAIL_PANEL_LABELS as LABELS, MACHINE_FORM_PAGE_LABELS, SPECIFICATION_LABELS } from '../../../utils/labels';
import { useNotification } from '../../../context/useNotification';
import { getOrderMailHref, getReturnMailHref } from '../machineEmailHref';
import AllBookingsView from './AllBookingsView';
import BookingCard from './BookingCard';
import DetailActionDialog from './DetailActionDialog';

type PendingEmail = 'order' | 'return' | 'return_unconfirm' | null;

interface MachineDetailPanelProps {
  machine: Machine;
  machinesReadOnly: boolean;
  bookingsReadOnly: boolean;
  projectName?: string;
  suppliers: Supplier[];
  categories: Category[];
  options: MachineOption[];
  orgSettings: Pick<
    Settings,
    'order_email_subject' | 'order_email_body' | 'return_email_subject' | 'return_email_body'
  >;
  isMobile: boolean;
  onClose: () => void;
  onUpdateMachine: (
    id: string,
    updates: MachineUpdate,
    related?: { supplier?: Machine['supplier'] }
  ) => Promise<void>;
  onDeleteMachine: (id: string) => Promise<void>;
  onAddBooking: () => void;
  onSelectBooking: (bookingId: string, machineId: string) => void;
  onAddSupplier: (name: string, email: string) => Promise<Supplier | null>;
  onAddCategory: (name: string) => Promise<Category | null>;
  onAddOption: (name: string) => Promise<MachineOption | null>;
  pickupHandinEnabled: boolean;
}

export default function MachineDetailPanel({
  machine,
  machinesReadOnly,
  bookingsReadOnly,
  projectName = '',
  suppliers,
  categories,
  options,
  orgSettings,
  isMobile,
  onClose,
  onUpdateMachine,
  onDeleteMachine,
  onAddBooking,
  onSelectBooking,
  onAddSupplier,
  onAddCategory,
  onAddOption,
  pickupHandinEnabled,
}: MachineDetailPanelProps) {
  const [moreInfoOpen, setMoreInfoOpen] = useState(false);
  const [allBookingsOpen, setAllBookingsOpen] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<PendingEmail>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [detailAction, setDetailAction] = useState<DetailActionType | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingDeleteMachine, setPendingDeleteMachine] = useState(false);

  const { mode } = useColorScheme();
  const isDark = mode === 'dark';
  const { notifySuccess, notifyError } = useNotification();

  const orderMailHref = getOrderMailHref(machine, projectName, orgSettings);
  const returnMailHref = getReturnMailHref(machine, projectName, orgSettings);
  const supplierEmail = machine.supplier?.email;

  function openMailto(href: string | null) {
    if (!href) return;
    const a = document.createElement('a');
    a.href = href;
    a.click();
  }

  function closeMenu() {
    setMenuAnchor(null);
  }

  async function handleSave(updates: MachineUpdate) {
    setSaving(true);
    const related: { supplier?: Machine['supplier'] } = {};
    if (updates.supplier_id !== undefined) {
      const sup = updates.supplier_id ? suppliers.find((s) => s.id === updates.supplier_id) : null;
      related.supplier = sup ? { id: sup.id, name: sup.name, email: sup.email } : undefined;
    }
    try {
      await onUpdateMachine(machine.id, updates, related);
      setDetailAction(null);
      notifySuccess(LABELS.updateSuccess);
    } catch {
      notifyError(LABELS.mutationError);
    } finally {
      setSaving(false);
    }
  }

  // Order email flow
  function handleOrderStatusConfirm() {
    onUpdateMachine(machine.id, { status: 'ordered' })
      .then(() => notifySuccess(LABELS.statusUpdateSuccess))
      .catch(() => notifyError(LABELS.mutationError));
    setPendingEmail(null);
  }
  function handleOrderStatusDismiss() {
    setPendingEmail(null);
  }

  // Pickup confirm flow (after mailing the supplier)
  function handlePickupConfirmConfirm() {
    onUpdateMachine(machine.id, { pickup_on_end_date: true })
      .then(() => notifySuccess(LABELS.pickupConfirmSuccess))
      .catch(() => notifyError(LABELS.mutationError));
    setPendingEmail(null);
  }
  function handlePickupConfirmDismiss() {
    setPendingEmail(null);
  }

  // Pickup unconfirm flow
  function handlePickupRemoveConfirm() {
    onUpdateMachine(machine.id, { pickup_on_end_date: false })
      .then(() => notifySuccess(LABELS.pickupRemoveSuccess))
      .catch(() => notifyError(LABELS.mutationError));
    setPendingEmail(null);
  }
  function handlePickupRemoveAndMail() {
    onUpdateMachine(machine.id, { pickup_on_end_date: false })
      .then(() => notifySuccess(LABELS.pickupRemoveSuccess))
      .catch(() => notifyError(LABELS.mutationError));
    openMailto(returnMailHref);
    setPendingEmail(null);
  }

  const showOrderEmail = machine.status === 'to_order' && !!supplierEmail;
  const showPickupConfirm = !machine.pickup_on_end_date && machine.status !== 'to_order';
  const showPickupRemove = !!machine.pickup_on_end_date;
  const canReturnMail = !!supplierEmail && !!returnMailHref;

  const availability = getMachineAvailability(machine, pickupHandinEnabled);
  const avail = AVAILABILITY[availability];
  const currentBookingGroup = isDark ? bookingGroupDark : bookingGroup;

  // Required-ness is looked up on the machine's *current* category, not stored on the value
  // itself — a spec value that's still present but no longer marked required (or whose category
  // link changed) falls back to the optional/"meer info" tier instead of disappearing.
  const specValues = machine.specifications ?? [];
  const requiredSpecKeySet = new Set(
    (categories.find((c) => c.id === machine.category_id)?.specifications ?? [])
      .filter((s) => s.required)
      .map((s) => s.key)
  );
  const presentSpecKeys = new Set(specValues.map((s) => s.key));
  const specValueByKey = Object.fromEntries(specValues.map((s) => [s.key, s.value]));
  const requiredSpecKeys = SPECIFICATION_KEYS.filter((k) => presentSpecKeys.has(k) && requiredSpecKeySet.has(k));
  const optionalSpecKeys = SPECIFICATION_KEYS.filter((k) => presentSpecKeys.has(k) && !requiredSpecKeySet.has(k));

  const FUTURE_LIMIT = 1;

  const previewGroups: { key: BookingPeriod; label: string; emptyText: string; color: string }[] = [
    {
      key: 'active',
      label: LABELS.groupActive,
      emptyText: LABELS.noActiveBookings,
      color: currentBookingGroup.active,
    },
    {
      key: 'future',
      label: LABELS.groupFuture,
      emptyText: LABELS.noFutureBookings,
      color: currentBookingGroup.future,
    },
  ];

  if (allBookingsOpen) {
    return (
      <AllBookingsView
        machine={machine}
        pickupHandinEnabled={pickupHandinEnabled}
        onBack={() => setAllBookingsOpen(false)}
        onClose={onClose}
        onSelectBooking={onSelectBooking}
      />
    );
  }

  return (
    <>
      {/* Header */}
      <div className="flex items-start gap-2 px-4 py-3 shrink-0 border-b border-border">
        {isMobile && (
          <Tooltip title={LABELS.close}>
            <IconButton onClick={onClose} color="inherit" aria-label={LABELS.close} edge="start">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        )}
        <div className="flex flex-col min-w-0 flex-1">
          <p id="detail-machine-name" className="text-lg font-semibold mb-0 mt-0 truncate">
            {machine.name}
          </p>
          {machine.category && (
            <span className="text-sm text-text-secondary truncate">{machine.category}</span>
          )}
        </div>
        {!machinesReadOnly && (
          <Tooltip title={LABELS.actionsMenu}>
            <IconButton
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              color="inherit"
              aria-label={LABELS.actionsMenu}
              aria-haspopup="true"
              aria-expanded={!!menuAnchor}
              data-testid="actions-menu-button"
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        )}
        {!isMobile && (
          <Tooltip title={LABELS.close}>
            <IconButton onClick={onClose} color="inherit" aria-label={LABELS.close}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        )}
      </div>

      {/* Info + Bookings — één scrollbaar blok */}
      <div className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-2">
            {/* Beschikbaarheid — dot + label */}
            <div className="flex flex-col">
              <span className="text-sm text-text-secondary">{LABELS.availability}</span>
              <div className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${avail.dot}`} />
                <span className="text-text-secondary">{avail.label}</span>
              </div>
            </div>

            <div className="flex flex-col">
              <span className="text-sm text-text-secondary">{LABELS.status}</span>
              <span className="text-base">
                {STATUSES[machine.status]?.label ?? machine.status}
              </span>
            </div>

            {machine.machine_number && (
              <div className="flex flex-col">
                <span className="text-sm text-text-secondary">{LABELS.machineNumber}</span>
                <span className="text-base">{machine.machine_number}</span>
              </div>
            )}

            {(machine.options?.length ?? 0) > 0 && (
              <div className="flex flex-col">
                <span className="text-sm text-text-secondary">{LABELS.options}</span>
                <ExpandableChipList
                  items={machine.options!}
                  renderChip={(opt) => <Chip key={opt} size="small" variant="outlined" label={opt} className="first-letter:uppercase" />}
                  showMoreLabel={LABELS.showMoreChips}
                  showLessLabel={LABELS.showLessChips}
                  toggleTestId="toggle-options-button"
                />
              </div>
            )}

            {requiredSpecKeys.length > 0 && (
              <div className="flex flex-col">
                <span className="text-sm text-text-secondary">{LABELS.specifications}</span>
                <ExpandableChipList
                  items={requiredSpecKeys}
                  renderChip={(k) => (
                    <Chip
                      key={k}
                      size="small"
                      variant="outlined"
                      label={`${SPECIFICATION_LABELS[k]}: ${formatSpecificationValue(k, specValueByKey[k])}`}
                      data-testid={`spec-chip-${k}`}
                    />
                  )}
                  showMoreLabel={LABELS.showMoreChips}
                  showLessLabel={LABELS.showLessChips}
                  toggleTestId="toggle-specs-button"
                />
              </div>
            )}

            <div className="flex flex-col">
              <span className="text-sm text-text-secondary">{LABELS.period}</span>
              <div className="flex items-center gap-2 text-base">
                <span>{formatDateRange(machine.start_date ?? '', machine.start_time?.slice(0, 5), machine.end_date ?? '', machine.end_time?.slice(0, 5))}</span>
                {machine.pickup_on_end_date && (
                  <Tooltip title={LABELS.pickupOnEndDate}>
                    <span data-testid="pickup-icon" className="flex">
                      <EventAvailableIcon />
                    </span>
                  </Tooltip>
                )}
              </div>
            </div>

            <ButtonBase
              onClick={() => setMoreInfoOpen((v) => !v)}
              aria-expanded={moreInfoOpen}
              className="flex items-center gap-1.5 text-sm text-text-secondary rounded mt-1"
              data-testid="more-info-toggle"
            >
              <ExpandMoreIcon
                fontSize="small"
                className={`transition-transform duration-200 ${moreInfoOpen ? 'rotate-180' : ''}`}
              />
              {LABELS.moreInfo}
            </ButtonBase>

            <Collapse in={moreInfoOpen}>
              <div className="flex flex-col gap-2 pt-1">
                <div className="flex flex-col">
                  <span className="text-sm text-text-secondary">{LABELS.engineType}</span>
                  <span className="text-base">
                    {ENGINE_TYPES[machine.engine_type]?.label ?? machine.engine_type}
                  </span>
                </div>
                {optionalSpecKeys.map((k) => (
                  <div key={k} className="flex flex-col">
                    <span className="text-sm text-text-secondary">{SPECIFICATION_LABELS[k]}</span>
                    <span className="text-base" data-testid={`spec-value-${k}`}>
                      {formatSpecificationValue(k, specValueByKey[k])}
                    </span>
                  </div>
                ))}
                {machine.supplier && (
                  <div className="flex flex-col">
                    <span className="text-sm text-text-secondary">{LABELS.supplier}</span>
                    <span className="text-base">{machine.supplier.name}</span>
                  </div>
                )}
                {machine.notes && (
                  <div className="flex flex-col">
                    <span className="text-sm text-text-secondary">
                      {MACHINE_FORM_PAGE_LABELS.notesLabel}
                    </span>
                    <span className="text-base">{machine.notes}</span>
                  </div>
                )}
              </div>
            </Collapse>
          </div>

          {/* Bookings */}
          <div className="p-4 space-y-4 border-t border-border">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wider text-text-secondary">
                {LABELS.bookingsTitle} ({machine.bookings?.length ?? 0})
              </span>
              {!bookingsReadOnly && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={onAddBooking} data-testid="add-booking-button">
                  {LABELS.addBooking}
                </Button>
              )}
            </div>

            {machine.bookings?.length === 0 && (
              <p className="text-xs italic text-text-secondary">{LABELS.noBookings}</p>
            )}

            {(machine.bookings?.length ?? 0) > 0 &&
              previewGroups.map(({ key, label: groupLabel, emptyText, color: groupColor }) => {
                const sorted = (machine.bookings ?? [])
                  .filter((bk) => getBookingPeriod(bk, pickupHandinEnabled) === key)
                  .sort((a, b) => a.start_date.localeCompare(b.start_date));
                const displayed = key === 'future' ? sorted.slice(0, FUTURE_LIMIT) : sorted;
                const hiddenCount = key === 'future' ? sorted.length - FUTURE_LIMIT : 0;

                return (
                  <div key={key} className="mb-4">
                    <p
                      className="text-xs font-bold uppercase tracking-[0.08em] mb-1.5"
                      style={{ color: groupColor }}
                    >
                      {groupLabel}
                    </p>

                    {sorted.length === 0 && (
                      <p className="text-xs italic text-text-secondary">{emptyText}</p>
                    )}

                    <div className="space-y-2">
                      {displayed.map((bk) => (
                        <BookingCard
                          key={bk.id}
                          booking={bk}
                          showNotes={false}
                          pickupState={getBookingPickupState(bk, pickupHandinEnabled)}
                          isOverdue={isBookingOverdueReturn(bk, pickupHandinEnabled)}
                          onCardClick={(bk) => onSelectBooking(bk.id, machine.id)}
                        />
                      ))}
                    </div>

                    {hiddenCount > 0 && (
                      <p className="text-xs italic text-text-secondary mt-1">
                        {LABELS.moreBookings(hiddenCount)}
                      </p>
                    )}
                  </div>
                );
              })}

            {(machine.bookings?.length ?? 0) > 0 && (
              <Button
                onClick={() => setAllBookingsOpen(true)}
                variant="text"
                fullWidth
                color="primary"
                size="small"
                data-testid="show-all-bookings-button"
              >
                {LABELS.showAllBookings}
              </Button>
            )}
          </div>
        </div>

      {/* Actiemenu */}
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            closeMenu();
            setDetailAction('status');
          }}
          data-testid="action-status"
        >
          <ListItemIcon>
            <TuneIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{LABELS.actionStatus}</ListItemText>
        </MenuItem>

        {showPickupConfirm && (
          <MenuItem
            onClick={() => {
              closeMenu();
              onUpdateMachine(machine.id, { pickup_on_end_date: true })
                .then(() => notifySuccess(LABELS.pickupConfirmSuccess))
                .catch(() => notifyError(LABELS.mutationError));
            }}
            data-testid="action-pickup-confirm"
          >
            <ListItemIcon>
              <EventAvailableIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>{LABELS.actionPickupConfirm}</ListItemText>
          </MenuItem>
        )}

        {showPickupConfirm && canReturnMail && (
          <MenuItem
            onClick={() => {
              closeMenu();
              openMailto(returnMailHref);
              setPendingEmail('return');
            }}
            data-testid="action-pickup-confirm-mail"
          >
            <ListItemIcon>
              <MailOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{LABELS.actionPickupConfirmMail}</ListItemText>
          </MenuItem>
        )}

        {showPickupRemove && (
          <MenuItem
            onClick={() => {
              closeMenu();
              setPendingEmail('return_unconfirm');
            }}
            data-testid="action-pickup-remove"
          >
            <ListItemIcon>
              <EventBusyIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>{LABELS.actionPickupRemove}</ListItemText>
          </MenuItem>
        )}

        {showOrderEmail && (
          <MenuItem
            onClick={() => {
              closeMenu();
              openMailto(orderMailHref);
              setPendingEmail('order');
            }}
            data-testid="action-order-email"
          >
            <ListItemIcon>
              <AddShoppingCartIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{LABELS.actionOrderEmail}</ListItemText>
          </MenuItem>
        )}

        <Divider />

        <MenuItem
          onClick={() => {
            closeMenu();
            setDetailAction('machine_details');
          }}
          data-testid="action-edit-machine"
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{LABELS.actionMachineDetails}</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            closeMenu();
            setDetailAction('machine_number');
          }}
          data-testid="action-edit-machine-number"
        >
          <ListItemIcon>
            <TagIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{LABELS.actionMachineNumber}</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            closeMenu();
            setDetailAction('period');
          }}
          data-testid="action-edit-period"
        >
          <ListItemIcon>
            <CalendarMonthIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{LABELS.actionPeriod}</ListItemText>
        </MenuItem>

        <Divider />

        {(machine.inactive || availability !== 'inactive') && (
          <MenuItem
            onClick={() => {
              closeMenu();
              const nextInactive = !machine.inactive;
              onUpdateMachine(machine.id, { inactive: nextInactive })
                .then(() => notifySuccess(nextInactive ? LABELS.toggleInactiveSuccess : LABELS.toggleActiveSuccess))
                .catch(() => notifyError(LABELS.mutationError));
            }}
            data-testid="action-toggle-inactive"
          >
            <ListItemIcon>
              {machine.inactive ? (
                <PlayCircleOutlineIcon fontSize="small" color="success" />
              ) : (
                <PauseCircleOutlineIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>
              {machine.inactive ? LABELS.actionReactivate : LABELS.actionToggleInactive}
            </ListItemText>
          </MenuItem>
        )}

        {!machine.bookings?.length && (
          <MenuItem
            onClick={() => {
              closeMenu();
              setPendingDeleteMachine(true);
            }}
            data-testid="action-delete-machine"
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>{LABELS.actionDelete}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Dialogen */}

      {detailAction && (
        <DetailActionDialog
          action={detailAction}
          machine={machine}
          suppliers={suppliers}
          categories={categories}
          options={options}
          saving={saving}
          onClose={() => setDetailAction(null)}
          onSave={handleSave}
          onAddSupplier={onAddSupplier}
          onAddCategory={onAddCategory}
          onAddOption={onAddOption}
        />
      )}

      {pendingEmail === 'order' && (
        <AppDialog
          open
          onClose={handleOrderStatusDismiss}
          maxWidth="xs"
          fullWidth
          fullScreenOnMobile={false}
          title={LABELS.orderEmailDialogTitle}
          testId="order-status-dialog"
        >
          <DialogContent>
            <p className="mb-0 mt-0">{LABELS.orderEmailDialogBody}</p>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleOrderStatusDismiss} color="inherit" data-testid="dismiss-button">
              {LABELS.emailDialogNo}
            </Button>
            <Button onClick={handleOrderStatusConfirm} variant="contained" color="primary" data-testid="save-button">
              {LABELS.emailDialogYes}
            </Button>
          </DialogActions>
        </AppDialog>
      )}

      {pendingEmail === 'return' && (
        <AppDialog
          open
          onClose={handlePickupConfirmDismiss}
          maxWidth="xs"
          fullWidth
          fullScreenOnMobile={false}
          title={LABELS.orderEmailDialogTitle}
          testId="pickup-confirm-mail-dialog"
        >
          <DialogContent>
            <p className="mb-0 mt-0">{LABELS.pickupConfirmEmailDialogBody}</p>
          </DialogContent>
          <DialogActions>
            <Button onClick={handlePickupConfirmDismiss} color="inherit" data-testid="dismiss-button">
              {LABELS.emailDialogNo}
            </Button>
            <Button onClick={handlePickupConfirmConfirm} variant="contained" color="primary" data-testid="save-button">
              {LABELS.emailDialogYes}
            </Button>
          </DialogActions>
        </AppDialog>
      )}

      {pendingEmail === 'return_unconfirm' && (
        <AppDialog
          open
          onClose={() => setPendingEmail(null)}
          maxWidth="xs"
          fullWidth
          fullScreenOnMobile={false}
          title={LABELS.pickupRemoveDialogTitle}
          testId="pickup-remove-dialog"
        >
          <DialogContent>
            <p className="mb-0 mt-0">{LABELS.pickupRemoveDialogBody}</p>
          </DialogContent>
          <DialogActions>
            {canReturnMail && (
              <Button onClick={handlePickupRemoveAndMail} color="inherit">
                {LABELS.pickupRemoveMail}
              </Button>
            )}
            <Button onClick={handlePickupRemoveConfirm} variant="contained" color="primary" data-testid="save-button">
              {LABELS.pickupRemoveConfirm}
            </Button>
          </DialogActions>
        </AppDialog>
      )}

      <ConfirmDeleteDialog
        open={pendingDeleteMachine}
        onClose={() => setPendingDeleteMachine(false)}
        onConfirm={() => {
          setPendingDeleteMachine(false);
          onDeleteMachine(machine.id)
            .then(() => notifySuccess(LABELS.machineDeleteSuccess))
            .catch(() => notifyError(LABELS.mutationError));
          onClose();
        }}
      />
    </>
  );
}
